import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
    allImplantsGetTypes,
    allImplantsGetManufacturers,
    allImplantsGetImplantsByType,
    allImplantsGetImplantsByManufacturer,
} from '../../services/apiService';

export interface ImplantBrief {
    _id: string;
    Model: string;
    Type?: string;
    'MRI-status'?: number;
}

type Mode = 'by-type' | 'by-manufacturer';

interface ImplantCatalogPickerProps {
    open: boolean;
    existingSet: Set<string>;
    selectedItems: Map<string, ImplantBrief>;
    onToggleItem: (implant: ImplantBrief) => void;
    onSelectMany: (items: ImplantBrief[], select: boolean) => void;
}

const groupCacheKey = (mode: Mode, label: string) => `${mode}:${label}`;

export const ImplantCatalogPicker = ({
    open,
    existingSet,
    selectedItems,
    onToggleItem,
    onSelectMany,
}: ImplantCatalogPickerProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [mode, setMode] = useState<Mode>('by-type');
    const [groupLabels, setGroupLabels] = useState<{
        type: string[];
        manufacturer: string[];
    }>({ type: [], manufacturer: [] });
    const [groupItems, setGroupItems] = useState<Map<string, ImplantBrief[]>>(
        new Map()
    );
    const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [groupSearch, setGroupSearch] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        const load = async () => {
            setLoading(true);
            setMode('by-type');
            setExpanded(new Set());
            setGroupItems(new Map());
            setLoadingGroups(new Set());
            setGroupSearch('');
            try {
                const [types, manufacturers] = await Promise.all([
                    allImplantsGetTypes(),
                    allImplantsGetManufacturers(),
                ]);
                setGroupLabels({ type: types, manufacturer: manufacturers });
            } catch {
                toast({
                    title: t('folders.common.error'),
                    description: t(
                        'folders.manageImplants.toasts.loadCatalogFailed'
                    ),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open]);

    useEffect(() => {
        setExpanded(new Set());
        setGroupSearch('');
    }, [mode]);

    const loadGroup = async (
        m: Mode,
        label: string
    ): Promise<ImplantBrief[] | null> => {
        const key = groupCacheKey(m, label);
        const cached = groupItems.get(key);
        if (cached) return cached;
        if (loadingGroups.has(key)) return null;

        setLoadingGroups((prev) => new Set(prev).add(key));
        try {
            const implants: ImplantBrief[] =
                m === 'by-type'
                    ? await allImplantsGetImplantsByType(label)
                    : await allImplantsGetImplantsByManufacturer(label);
            setGroupItems((prev) => new Map(prev).set(key, implants));
            return implants;
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.manageImplants.toasts.loadGroupFailed'),
                variant: 'destructive',
            });
            return null;
        } finally {
            setLoadingGroups((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const toggleExpand = (label: string) => {
        const willExpand = !expanded.has(label);
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
        if (willExpand) loadGroup(mode, label);
    };

    const toggleGroup = async (m: Mode, label: string) => {
        const cached = groupItems.get(groupCacheKey(m, label));
        const items = cached ?? (await loadGroup(m, label));
        if (!items) return;

        const selectable = items.filter((i) => !existingSet.has(i._id));
        const allSelected =
            selectable.length > 0 &&
            selectable.every((i) => selectedItems.has(i._id));
        const isSelecting = !allSelected;

        onSelectMany(selectable, isSelecting);

        if (isSelecting) {
            setExpanded((prev) => {
                if (prev.has(label)) return prev;
                return new Set(prev).add(label);
            });
        }
    };

    const groupCheckState = (
        m: Mode,
        label: string
    ): boolean | 'indeterminate' => {
        const items = groupItems.get(groupCacheKey(m, label));
        if (!items) return false;
        const selectable = items.filter((i) => !existingSet.has(i._id));
        if (selectable.length === 0) return false;
        const selectedCount = selectable.filter((i) =>
            selectedItems.has(i._id)
        ).length;
        if (selectedCount === 0) return false;
        if (selectedCount === selectable.length) return true;
        return 'indeterminate';
    };

    const currentLabels =
        mode === 'by-type' ? groupLabels.type : groupLabels.manufacturer;
    const search = groupSearch.trim().toLowerCase();
    const filteredLabels = search
        ? currentLabels.filter((l) => l.toLowerCase().includes(search))
        : currentLabels;
    const searchPlaceholder =
        mode === 'by-type'
            ? t('folders.catalog.searchTypes')
            : t('folders.catalog.searchManufacturers');

    return (
        <section className="flex flex-col gap-3 md:w-3/5 md:min-w-0 md:overflow-hidden">
            <ToggleGroup
                type="single"
                value={mode}
                onValueChange={(v) => v && setMode(v as Mode)}
                className="justify-start"
            >
                <ToggleGroupItem value="by-type" className="h-8 text-xs">
                    {t('folders.catalog.byType')}
                </ToggleGroupItem>
                <ToggleGroupItem
                    value="by-manufacturer"
                    className="h-8 text-xs"
                >
                    {t('folders.catalog.byManufacturer')}
                </ToggleGroupItem>
            </ToggleGroup>

            {!loading && currentLabels.length > 0 && (
                <div className="relative">
                    <Search
                        aria-hidden="true"
                        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground"
                    />
                    <Input
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                        className="pl-9"
                    />
                </div>
            )}

            <div className="h-72 flex-1 overflow-y-auto md:h-auto md:min-h-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : filteredLabels.length === 0 ? (
                    <p className="py-4 text-sm text-subtle-foreground">
                        {currentLabels.length === 0
                            ? t('folders.catalog.noGroups')
                            : t('folders.catalog.noMatches')}
                    </p>
                ) : (
                    <ul>
                        {filteredLabels.map((label) => {
                            const key = groupCacheKey(mode, label);
                            const items = groupItems.get(key);
                            const isGroupLoading = loadingGroups.has(key);
                            const isExpanded = expanded.has(label);
                            const checkState = groupCheckState(mode, label);

                            return (
                                <li
                                    key={label}
                                    className="border-b border-subtle last:border-b-0"
                                >
                                    <div className="flex items-center gap-3 px-1 py-2">
                                        <Checkbox
                                            id={`group-${mode}-${label}`}
                                            checked={checkState}
                                            onCheckedChange={() =>
                                                toggleGroup(mode, label)
                                            }
                                            aria-label={t(
                                                'folders.catalog.selectAllAria',
                                                { label }
                                            )}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(label)}
                                            className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left text-sm"
                                        >
                                            <span
                                                className="min-w-0 flex-1 truncate"
                                                title={label}
                                            >
                                                {label}
                                            </span>
                                            <ChevronDown
                                                aria-hidden="true"
                                                className={cn(
                                                    'h-4 w-4 flex-shrink-0 text-subtle-foreground transition-transform',
                                                    isExpanded && 'rotate-180'
                                                )}
                                            />
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="pb-3 pl-7">
                                            {isGroupLoading ? (
                                                <div className="flex justify-center py-4">
                                                    <LoadingRing size="small" />
                                                </div>
                                            ) : !items || items.length === 0 ? (
                                                <p className="py-2 text-xs text-subtle-foreground">
                                                    {t(
                                                        'folders.catalog.noImplantsInGroup'
                                                    )}
                                                </p>
                                            ) : (
                                                <ul className="space-y-2 py-1">
                                                    {items.map((implant) => {
                                                        const alreadyAdded =
                                                            existingSet.has(
                                                                implant._id
                                                            );
                                                        const checked =
                                                            alreadyAdded ||
                                                            selectedItems.has(
                                                                implant._id
                                                            );
                                                        return (
                                                            <li
                                                                key={implant._id}
                                                                className="flex items-center gap-3"
                                                            >
                                                                <Checkbox
                                                                    id={`item-${implant._id}`}
                                                                    checked={
                                                                        checked
                                                                    }
                                                                    disabled={
                                                                        alreadyAdded
                                                                    }
                                                                    onCheckedChange={() =>
                                                                        onToggleItem(
                                                                            implant
                                                                        )
                                                                    }
                                                                    aria-label={t(
                                                                        'folders.catalog.selectItemAria',
                                                                        {
                                                                            model: implant.Model,
                                                                        }
                                                                    )}
                                                                />
                                                                <label
                                                                    htmlFor={`item-${implant._id}`}
                                                                    className="min-w-0 flex-1 cursor-pointer truncate text-sm text-foreground"
                                                                    title={
                                                                        implant.Model
                                                                    }
                                                                >
                                                                    {
                                                                        implant.Model
                                                                    }
                                                                </label>
                                                                {alreadyAdded && (
                                                                    <span className="flex-shrink-0 text-xs text-subtle-foreground">
                                                                        {t(
                                                                            'folders.catalog.alreadyAdded'
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
};
